import { formatDateTime } from 'utils/date.util';

const normalizeKey = (value) => String(value || '').toUpperCase().replace(/\s+/g, '_');

const RETURN_REASON_LABELS = {
  NO_LONGER_NEED_ITEM: "Just didn't like it",
  ORDERED_ACCIDENTALLY: 'Ordered accidentally',
  ORDERED_WRONG_ITEM: 'Ordered wrong item',
  DEFECTIVE_ITEM: 'Defective or damaged item',
  NOT_AS_DESCRIBED: 'Not as described',
  MISSING_PARTS: 'Missing parts or accessories',
  ARRIVED_DAMAGED: 'Arrived damaged',
  WRONG_SIZE: 'Wrong size',
  FOUND_BETTER_PRICE: 'Found better price',
  NO_LONGER_NEEDED: 'No longer needed',
};

const RETURN_REASON_TYPE_LABELS = {
  REMORSE: 'Buyer remorse',
  SNAD: 'Significantly not as described',
  CANCEL: 'Canceled after shipment',
  INSTORE: 'In-store return',
};

export const formatReturnReason = (reason) => {
  if (!reason) return null;
  const key = normalizeKey(reason);
  if (RETURN_REASON_LABELS[key]) return RETURN_REASON_LABELS[key];
  return formatCancelReason(reason);
};

const formatReturnReasonType = (reasonType) => {
  const key = normalizeKey(reasonType);
  if (RETURN_REASON_TYPE_LABELS[key]) return RETURN_REASON_TYPE_LABELS[key];
  return formatEnumLabel(reasonType);
};

const CANCEL_REASON_LABELS = {
  ADDRESS_ISSUES: 'Address issues',
  BUYER_ASKED_CANCEL: 'Buyer asked to cancel',
  FOUND_CHEAPER_PRICE: 'Found a cheaper price',
  ORDER_MISTAKE: 'Order placed by mistake',
  ORDER_UNPAID: 'Order unpaid',
  OUT_OF_STOCK_OR_CANNOT_FULFILL: 'Out of stock or cannot fulfill',
  UNKNOWN: 'Unknown',
  WONT_ARRIVE_IN_TIME: "Won't arrive in time",
  WRONG_PAYMENT_METHOD: 'Wrong payment method',
  WRONG_SHIPPING_ADDRESS: 'Wrong shipping address',
  WRONG_SHIPPING_METHOD: 'Wrong shipping method',
};

export const formatCancelReason = (reason) => {
  if (!reason) return null;
  const key = normalizeKey(reason);
  if (CANCEL_REASON_LABELS[key]) return CANCEL_REASON_LABELS[key];
  if (String(reason).includes(' ') && !String(reason).includes('_')) {
    const text = String(reason).trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

const formatCancelInitiator = (initiator) => {
  const key = normalizeKey(initiator);
  if (key === 'BUYER') return 'Buyer';
  if (key === 'SELLER') return 'Seller';
  return initiator || null;
};

const formatEnumLabel = (value) => {
  const key = normalizeKey(value);
  if (!key) return null;
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
};

export const getOrderReturnDetails = (order) => {
  const returnStatus = order?.returnStatus;
  if (!returnStatus) return null;

  const returnId = returnStatus.returnId;
  const reason = returnStatus.reason;
  if (!returnId && !reason) return null;

  return {
    returnId,
    status: returnStatus.status ? formatEnumLabel(returnStatus.status) : null,
    reason: reason ? formatReturnReason(reason) : null,
    reasonType: returnStatus.reasonType
      ? formatReturnReasonType(returnStatus.reasonType)
      : null,
    type: returnStatus.type ? formatEnumLabel(returnStatus.type) : null,
    comments: returnStatus.comments || null,
    creationDate: returnStatus.creationDate
      ? formatOrderDate(returnStatus.creationDate)
      : null,
    closedDate: returnStatus.closedDate ? formatOrderDate(returnStatus.closedDate) : null,
    refundAmount: returnStatus.refundAmount,
    refundCurrency: returnStatus.refundCurrency,
  };
};

export const getOrderCancelDetails = (order) => {
  const cancelStatus = order?.cancelStatus;
  if (!cancelStatus) return null;

  const cancelState = normalizeKey(cancelStatus.cancelState);
  if (!cancelState || cancelState === 'NONE_REQUESTED') return null;

  const requests = Array.isArray(cancelStatus.cancelRequests) ? cancelStatus.cancelRequests : [];
  const requestWithReason =
    [...requests].reverse().find((r) => r?.cancelReason) ||
    requests.find((r) => r?.cancelReason);
  const reason = requestWithReason?.cancelReason || cancelStatus.cancelReason;

  return {
    cancelState,
    cancelStateLabel: formatEnumLabel(cancelState),
    reason: reason ? formatCancelReason(reason) : null,
    initiator: formatCancelInitiator(requestWithReason?.cancelInitiator),
    cancelledDate: cancelStatus.cancelledDate
      ? formatOrderDate(cancelStatus.cancelledDate)
      : null,
    requests: requests.map((r) => ({
      reason: r.cancelReason ? formatCancelReason(r.cancelReason) : null,
      initiator: formatCancelInitiator(r.cancelInitiator),
      state: r.cancelRequestState ? formatEnumLabel(r.cancelRequestState) : null,
      requestedDate: r.cancelRequestedDate ? formatOrderDate(r.cancelRequestedDate) : null,
      completedDate: r.cancelCompletedDate ? formatOrderDate(r.cancelCompletedDate) : null,
    })),
  };
};

export const getOrderStatusTooltip = (order) => {
  if (!order) return null;

  const cancelDetails = getOrderCancelDetails(order);
  if (cancelDetails) {
    const lines = [];
    if (cancelDetails.reason) lines.push(`Reason: ${cancelDetails.reason}`);
    if (cancelDetails.initiator) lines.push(`Initiated by: ${cancelDetails.initiator}`);
    if (cancelDetails.cancelledDate) lines.push(`Canceled: ${cancelDetails.cancelledDate}`);
    if (lines.length) return lines.join('\n');
    return `Cancel status: ${cancelDetails.cancelStateLabel}`;
  }

  const returnDetails = getOrderReturnDetails(order);
  if (returnDetails) {
    const lines = [];
    if (returnDetails.reason) lines.push(`Reason: ${returnDetails.reason}`);
    if (returnDetails.reasonType) lines.push(`Type: ${returnDetails.reasonType}`);
    if (returnDetails.status) lines.push(`Return status: ${returnDetails.status}`);
    if (returnDetails.closedDate) lines.push(`Closed: ${returnDetails.closedDate}`);
    if (returnDetails.returnId) lines.push(`Return ID: ${returnDetails.returnId}`);
    if (lines.length) return lines.join('\n');
  }

  if (order.marketplace === 'amazon') {
    const status = order.orderStatus || order.orderFulfillmentStatus;
    return status || null;
  }

  return order.orderPaymentStatus || order.orderFulfillmentStatus || null;
};

export const orderMainStatusBadge = (order) => {
  if (!order) return { label: '—', colorScheme: 'gray' };

  if (order.marketplace === 'ebay') {
    const cancelState = normalizeKey(order.cancelStatus?.cancelState);
    if (cancelState && cancelState !== 'NONE_REQUESTED') {
      const cancelMap = {
        CANCELLED: { label: 'Canceled', colorScheme: 'red' },
        CANCELED: { label: 'Canceled', colorScheme: 'red' },
        CANCEL_PENDING: { label: 'Cancel pending', colorScheme: 'orange' },
        CANCEL_REQUESTED: { label: 'Cancel requested', colorScheme: 'orange' },
      };
      if (cancelMap[cancelState]) return cancelMap[cancelState];
      return {
        label: cancelState.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()),
        colorScheme: 'orange',
      };
    }

    if (getOrderReturnDetails(order)) {
      return { label: 'Returned', colorScheme: 'purple' };
    }

    const payment = normalizeKey(order.orderPaymentStatus);
    if (payment === 'FULLY_REFUNDED' || payment === 'PARTIALLY_REFUNDED') {
      return paymentStatusBadge(order.orderPaymentStatus);
    }

    return fulfillmentStatusBadge(order.orderFulfillmentStatus);
  }

  if (order.marketplace === 'amazon') {
    return fulfillmentStatusBadge(order.orderStatus || order.orderFulfillmentStatus);
  }

  return fulfillmentStatusBadge(order.orderStatus || order.orderFulfillmentStatus);
};

export const fulfillmentStatusBadge = (status) => {
  const key = String(status || '').toUpperCase();
  const map = {
    NOT_STARTED: { label: 'Not started', colorScheme: 'gray' },
    IN_PROGRESS: { label: 'In progress', colorScheme: 'blue' },
    FULFILLED: { label: 'Fulfilled', colorScheme: 'green' },
    CANCELLED: { label: 'Canceled', colorScheme: 'red' },
    Unshipped: { label: 'Unshipped', colorScheme: 'orange' },
    Shipped: { label: 'Shipped', colorScheme: 'green' },
    PartiallyShipped: { label: 'Partial', colorScheme: 'yellow' },
    Pending: { label: 'Pending', colorScheme: 'gray' },
    Canceled: { label: 'Canceled', colorScheme: 'red' },
  };
  return map[key] || { label: status || '—', colorScheme: 'gray' };
};

export const paymentStatusBadge = (status) => {
  const key = String(status || '').toUpperCase();
  const map = {
    PAID: { label: 'Paid', colorScheme: 'green' },
    PENDING: { label: 'Pending', colorScheme: 'yellow' },
    FAILED: { label: 'Failed', colorScheme: 'red' },
    PARTIALLY_REFUNDED: { label: 'Partial refund', colorScheme: 'orange' },
    FULLY_REFUNDED: { label: 'Refunded', colorScheme: 'purple' },
  };
  return map[key] || { label: status || '—', colorScheme: 'gray' };
};

export const formatOrderDate = (iso) => formatDateTime(iso);

export const formatMoney = (value, currency = 'USD') => {
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num);
};
